import { useState, useEffect, useCallback } from 'react';
import { getAllFXRates } from '../../../services/fxRateService';
import Table from '../../shared/UI/Table';
import Badge from '../../shared/UI/Badge';
import Button from '../../shared/UI/Button';

/**
 * Flatten the per-currency documents into one row per {currency, year}, newest
 * year first, so closed and provisional years are visible side by side.
 *
 * There is deliberately NO delete action. Firestore rules block deletes on this
 * collection — a delete would bypass the closed-year lock and destroy rate
 * history (Decision 4). The countries registry this mirrors has no delete
 * control either, so omitting it also matches the pattern.
 */
function toRows(docs) {
  const rows = [];
  docs.forEach(d => {
    Object.entries(d.rates || {}).forEach(([year, entry]) => {
      rows.push({
        id: `${d.currency_code}_${year}`,
        currency: d.currency_code || d.id,
        year: Number(year),
        rate: entry.rate,
        status: entry.status || 'provisional',
        source: entry.source || '—',
        note: entry.note || '',
        set_at: entry.set_at || null,
        config_version: d.config_version || 1,
        has_history: (d.config_history || []).length > 0,
      });
    });
  });
  return rows.sort((a, b) =>
    a.currency === b.currency ? b.year - a.year : a.currency.localeCompare(b.currency)
  );
}

export default function FXRateList({ onAdd, onEdit, onImport, refreshToken }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currencyFilter, setCurrencyFilter] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setRows(toRows(await getAllFXRates()));
      setError(null);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load, refreshToken]);

  const currencies = [...new Set(rows.map(r => r.currency))].sort();
  const displayed = currencyFilter
    ? rows.filter(r => r.currency === currencyFilter)
    : rows;

  const columns = [
    { key: 'currency', label: 'Currency' },
    { key: 'year', label: 'Year' },
    {
      key: 'rate',
      label: 'Year-end rate (USD per 1 unit)',
      render: row => (
        <span style={{ fontVariantNumeric: 'tabular-nums' }}>{row.rate}</span>
      ),
    },
    {
      key: 'status',
      label: 'Status',
      // Explicit variants: Badge's default map renders "closed" grey, which
      // reads as dormant. Here closed means settled and locked.
      render: row => row.status === 'closed'
        ? <Badge text="closed" variant="success" />
        : <Badge text="provisional" variant="warning" />,
    },
    { key: 'source', label: 'Source' },
    {
      key: 'history',
      label: 'History',
      render: row => row.has_history
        ? <Badge text={`v${row.config_version}`} variant="info" />
        : <span style={{ color: 'var(--color-text-secondary)' }}>—</span>,
    },
    {
      key: 'actions',
      label: 'Actions',
      render: row => (
        <Button size="sm" variant="secondary" onClick={() => onEdit(row)}>
          {row.status === 'closed' ? 'Correct' : 'Edit'}
        </Button>
      ),
    },
  ];

  if (error) return <div className="alert alert-error">{error}</div>;

  return (
    <div className="card">
      <div className="card-header">
        <h2>Year-end FX rates ({displayed.length})</h2>
        <div className="flex gap-3">
          {currencies.length > 1 && (
            <select
              className="form-select"
              value={currencyFilter}
              onChange={e => setCurrencyFilter(e.target.value)}
              style={{ maxWidth: 160 }}
            >
              <option value="">All currencies</option>
              {currencies.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          )}
          <Button variant="secondary" onClick={onImport}>Import rates</Button>
          <Button variant="primary" onClick={onAdd}>+ Add Rate</Button>
        </div>
      </div>

      <div style={{
        padding: '10px 16px', fontSize: 12,
        color: 'var(--color-text-secondary)',
        borderBottom: '1px solid var(--color-border)',
      }}>
        Rates are stored as <strong>USD per 1 unit</strong> of the currency (GHS ≈ 0.083,
        not 12) — the inverse of the usual quote. <strong>Closed</strong> years are locked:
        changing one records a correction with a reason and archives the previous value.
      </div>

      <Table
        columns={columns}
        data={displayed}
        loading={loading}
        emptyTitle="No year-end rates yet"
        emptyMessage="Add a rate, or import a table of currency/year/rate rows."
        emptyAction={{ label: '+ Add Rate', onClick: onAdd }}
      />
    </div>
  );
}
